import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  getFriends, getFriendRequests, acceptFriendRequest, rejectFriendRequest, removeFriend,
} from '../services/friendsApi';
import LeagueBadge from '../components/league/LeagueBadge';

function Friends() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [friendsData, requestsData] = await Promise.all([
          getFriends(),
          getFriendRequests(),
        ]);
        setFriends(friendsData.friends);
        setRequests(requestsData.requests);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  const handleAccept = async (friendshipId) => {
    setActionLoading(friendshipId);
    try {
      await acceptFriendRequest(friendshipId);
      // Move from requests to friends
      const accepted = requests.find((r) => r.friendshipId === friendshipId);
      setRequests((prev) => prev.filter((r) => r.friendshipId !== friendshipId));
      if (accepted) {
        setFriends((prev) => [...prev, {
          friendshipId,
          id: accepted.id,
          username: accepted.username,
          displayName: accepted.displayName,
          avatar: accepted.avatar,
          leagueTier: accepted.leagueTier,
        }]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (friendshipId) => {
    setActionLoading(friendshipId);
    try {
      await rejectFriendRequest(friendshipId);
      setRequests((prev) => prev.filter((r) => r.friendshipId !== friendshipId));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (friendshipId) => {
    setActionLoading(friendshipId);
    try {
      await removeFriend(friendshipId);
      setFriends((prev) => prev.filter((f) => f.friendshipId !== friendshipId));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const renderAvatar = (person) => (
    <div className="w-10 h-10 rounded-full bg-puzzle-muted overflow-hidden flex items-center justify-center flex-shrink-0">
      {person.avatar ? (
        <img
          src={person.avatar}
          alt={person.username}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-sm font-semibold text-puzzle-text-secondary">
          {person.username.slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
  );

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-puzzle-text mb-2">Friends</h1>
          <Link to="/search" className="text-puzzle-blue hover:underline text-sm">
            Find players
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-puzzle-surface rounded-lg shadow p-1 gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('friends')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'friends'
                  ? 'bg-puzzle-blue text-white'
                  : 'text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover'
              }`}
            >
              My Friends
              {friends.length > 0 && (
                <span className="ml-1.5 text-xs opacity-80">
                  (
                  {friends.length}
                  )
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors relative ${
                activeTab === 'requests'
                  ? 'bg-puzzle-blue text-white'
                  : 'text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover'
              }`}
            >
              Requests
              {requests.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-red-500 text-white rounded-full">
                  {requests.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-puzzle-surface rounded-lg shadow overflow-hidden">
          {activeTab === 'friends' && (
            friends.length > 0 ? (
              <div className="divide-y divide-puzzle-border">
                {friends.map((friend) => (
                  <div key={friend.friendshipId} className="flex items-center gap-3 px-4 py-3">
                    <Link to={`/profile/${friend.username}`} className="flex items-center gap-3 flex-1 min-w-0">
                      {renderAvatar(friend)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-puzzle-text truncate">
                          {friend.username}
                        </div>
                        {friend.displayName && (
                          <div className="text-sm text-puzzle-text-secondary truncate">
                            {friend.displayName}
                          </div>
                        )}
                      </div>
                      {friend.leagueTier && (
                        <LeagueBadge tier={friend.leagueTier} size="sm" />
                      )}
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleRemove(friend.friendshipId)}
                      disabled={actionLoading === friend.friendshipId}
                      className="px-3 py-1.5 text-xs font-medium text-puzzle-text-secondary border border-puzzle-border rounded-lg hover:text-red-600 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                    >
                      {actionLoading === friend.friendshipId ? '...' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <p className="text-puzzle-text-secondary mb-2">No friends yet</p>
                <Link to="/search" className="text-puzzle-blue hover:underline text-sm">
                  Find players to add
                </Link>
              </div>
            )
          )}

          {activeTab === 'requests' && (
            requests.length > 0 ? (
              <div className="divide-y divide-puzzle-border">
                {requests.map((request) => (
                  <div key={request.friendshipId} className="flex items-center gap-3 px-4 py-3">
                    <Link to={`/profile/${request.username}`} className="flex items-center gap-3 flex-1 min-w-0">
                      {renderAvatar(request)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-puzzle-text truncate">
                          {request.username}
                        </div>
                        {request.displayName && (
                          <div className="text-sm text-puzzle-text-secondary truncate">
                            {request.displayName}
                          </div>
                        )}
                      </div>
                    </Link>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleAccept(request.friendshipId)}
                        disabled={actionLoading === request.friendshipId}
                        className="px-3 py-1.5 text-xs font-medium bg-puzzle-blue text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        {actionLoading === request.friendshipId ? '...' : 'Accept'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(request.friendshipId)}
                        disabled={actionLoading === request.friendshipId}
                        className="px-3 py-1.5 text-xs font-medium text-puzzle-text-secondary border border-puzzle-border rounded-lg hover:bg-puzzle-hover transition-colors"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 px-4">
                <p className="text-puzzle-text-secondary">No pending requests</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default Friends;
